import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { UserInfo, UserMode, UserModeInfo } from '@/types/auth.types';
import authService from '@/services/auth.service';
import tenantStatusService from '@/services/tenant-status.service';
import { trackLogin, trackLogout } from '@/utils/analytics';
import { setSentryUser, setSentryUserMode } from '@/utils/sentry';
import permissionRoutes from '@/constants/constant-permission-route-mapping';
import { AUTH_HANDLE_TOKEN } from '@/constants/constant-routes';

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userMode: UserModeInfo;
  tenantKey: string | null;
  tenantStatus: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | null;
  isManagementTenant: boolean;
  login: (credentials: any) => Promise<{ user: UserInfo }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  refreshPermissions: () => Promise<boolean>;
  checkTenantStatus: () => Promise<boolean>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  getUserGroups: () => string[];
  getUserRoles: () => string[];
  switchMode: (mode: UserMode) => void;
  canSwitchMode: () => boolean;
  updateUser: (partial: Partial<UserInfo>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const location = useLocation();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tenantKey, setTenantKey] = useState<string | null>(null);
  const [tenantStatus, setTenantStatus] = useState<'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | null>(null);
  const [userMode, setUserMode] = useState<UserModeInfo>({
    hasAdminRole: false,
    hasLearnerRole: false,
    hasBothRoles: false,
    currentMode: 'learner',
    preferredMode: 'learner',
  });

  // Ref to track latest isAuthenticated for intervals (avoids re-running initializeAuth)
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Track whether initializeAuth is still running (prevents concurrent /me from checkAuth)
  const initPromiseRef = useRef<Promise<void> | null>(null);

  // Keep the Sentry `user_mode` tag in sync with the current mode. One effect
  // covers every path (login, initializeAuth, checkAuth, switchMode, logout)
  // — no need to scatter setSentryUserMode calls across callsites.
  useEffect(() => {
    setSentryUserMode(isAuthenticated ? userMode.currentMode : null);
  }, [isAuthenticated, userMode.currentMode]);

  // Sync mode based on URL path
  useEffect(() => {
    if (!isAuthenticated || !userMode.hasBothRoles) return;

    const path = location.pathname;

    if (path.startsWith('/admin') && userMode.currentMode !== 'admin') {
      setUserMode(prev => ({ ...prev, currentMode: 'admin' }));
      localStorage.setItem('skillzen_user_mode', 'admin');
    } else if (path.startsWith('/learn') && userMode.currentMode !== 'learner') {
      setUserMode(prev => ({ ...prev, currentMode: 'learner' }));
      localStorage.setItem('skillzen_user_mode', 'learner');
    }
  }, [location.pathname, isAuthenticated, userMode.hasBothRoles]);

  // Check if current hostname is management tenant (management-dev or management)
  const isManagementTenant = (() => {
    const hostname = window.location.hostname;
    return hostname.includes('management-dev') || hostname.includes('management');
  })();

  // Initialize auth state on app load (runs ONCE on mount)
  useEffect(() => {
    initPromiseRef.current = initializeAuth().finally(() => {
      initPromiseRef.current = null;
    });

    // Monitor localStorage changes (e.g., manual token deletion)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.includes('skillzen_user')) {
        if (!e.newValue && e.oldValue) {
          // User data was removed from localStorage (logged out in another tab)
          handleUnauthenticated();
          window.location.replace('/login');
        }
      }
    };

    // Monitor localStorage across tabs
    window.addEventListener('storage', handleStorageChange);

    // Check tenant status periodically (every 5 minutes)
    // This detects mid-session status changes (e.g., admin suspends tenant)
    const tenantStatusCheckInterval = setInterval(async () => {
      if (isAuthenticatedRef.current && !isManagementTenant) {
        await checkTenantStatus();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(tenantStatusCheckInterval);
    };
  }, []);

  const initializeAuth = async () => {
    setIsLoading(true);
    try {
      // Check tenant status first (unless management tenant)
      if (!isManagementTenant) {
        try {
          const status = await tenantStatusService.checkStatus();
          setTenantStatus(status.status);

          if (!status.can_login) {
            console.warn(`[AuthContext] Tenant is ${status.status}, redirecting to /tenant-blocked`);
            handleUnauthenticated();
            // Only redirect if not already on tenant-blocked page (prevents infinite loop)
            if (!window.location.pathname.includes('/tenant-blocked')) {
              window.location.replace('/tenant-blocked');
            }
            return;
          }
        } catch (statusError) {
          console.warn('[AuthContext] Failed to check tenant status during init:', statusError);
          // Continue with auth check - backend will handle validation
        }
      }

      // Check if we have local session indicators (user data or CSRF token)
      const hasLocalSession = authService.isAuthenticated();
      // SSO flow: server sets HttpOnly cookies on redirect to /handle-token,
      // but local storage is empty. We must try /me before giving up.
      const isSsoCallback = window.location.pathname === AUTH_HANDLE_TOKEN;

      if (hasLocalSession || isSsoCallback) {
        const storedUser = authService.getUser();

        if (storedUser && hasLocalSession) {
          setUser(storedUser);
          setSentryUser({ id: storedUser.id, email: storedUser.email, name: storedUser.full_name || storedUser.given_name });

          // Ensure permissions are in sessionStorage BEFORE setting authenticated
          // (sessionStorage is tab-scoped, so new tabs start empty)
          if (!sessionStorage.getItem('permissions')) {
            try {
              const meData = await authService.getCurrentUser();
              sessionStorage.setItem('permissions', JSON.stringify(meData?.permissions || []));
            } catch (err) {
              console.warn('Failed to fetch permissions during init:', err);
              sessionStorage.setItem('permissions', JSON.stringify([]));
            }
          }

          // Derive user mode from permissions (after permissions are loaded)
          const modeInfo = detectUserRoles();
          setUserMode(modeInfo);

          setIsAuthenticated(true);
        } else {
          // No local user data — fetch from server (SSO flow or partial state)
          try {
            const currentUserData = await authService.getCurrentUser();
            if (currentUserData) {
              setUser(currentUserData.user);
              setTenantKey(currentUserData.tenant_key || null);
              setSentryUser({
                id: currentUserData.user.id,
                email: currentUserData.user.email,
                name: currentUserData.user.full_name || currentUserData.user.given_name,
              });

              // Store permissions BEFORE setting authenticated
              sessionStorage.setItem('permissions', JSON.stringify(currentUserData.permissions || []));

              // Derive user mode from permissions (after permissions are loaded)
              const modeInfo = detectUserRoles();
              setUserMode(modeInfo);

              setIsAuthenticated(true);
            } else {
              handleUnauthenticated();
            }
          } catch (error) {
            console.warn('Failed to fetch user data during initialization:', error);
            handleUnauthenticated();
          }
        }
      } else {
        handleUnauthenticated();
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      handleUnauthenticated();
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnauthenticated = () => {
    setSentryUser(null);
    setUser(null);
    setIsAuthenticated(false);
    setTenantKey(null);
    authService.clearSession();
    setUserMode({
      hasAdminRole: false,
      hasLearnerRole: false,
      hasBothRoles: false,
      currentMode: 'learner',
      preferredMode: 'learner',
    });
  };

  // Derive user roles from permissions (not hardcoded group names)
  const detectUserRoles = (): UserModeInfo => {
    const userPermissions: string[] = JSON.parse(
      sessionStorage.getItem('permissions') || '[]'
    );

    // Check if user has any permission mapped to an admin or learner route
    const hasAdmin = userPermissions.some(p =>
      permissionRoutes[p]?.startsWith('/admin')
    );
    const hasLearner = userPermissions.some(p =>
      permissionRoutes[p]?.startsWith('/learn')
    );
    const hasValidRole = userPermissions.length > 0;

    // Get stored preferences
    const storedMode = localStorage.getItem('skillzen_user_mode') as UserMode;
    const storedPreferred = localStorage.getItem('skillzen_preferred_mode') as UserMode;

    // Determine default mode
    let defaultMode: UserMode = 'learner';

    if (hasAdmin && !hasLearner) {
      defaultMode = 'admin';
    } else if (hasLearner && !hasAdmin) {
      defaultMode = 'learner';
    } else if (hasAdmin && hasLearner) {
      defaultMode = storedMode || storedPreferred || 'admin';
    }

    return {
      hasAdminRole: hasAdmin,
      hasLearnerRole: hasLearner,
      hasBothRoles: hasAdmin && hasLearner,
      currentMode: storedMode || defaultMode,
      preferredMode: storedPreferred || defaultMode,
      hasValidRole,
    };
  };

  const login = async (credentials: any) => {
    setIsLoading(true);
    try {
      const result = await authService.login(credentials);

      // Fetch permissions from /me BEFORE setting auth state, so sidebar has them when it mounts
      try {
        const meData = await authService.getCurrentUser();
        sessionStorage.setItem('permissions', JSON.stringify(meData?.permissions || []));
      } catch (err) {
        console.error('Failed to fetch permissions:', err);
        sessionStorage.setItem('permissions', JSON.stringify([]));
      }

      setUser(result.user);

      // Derive user mode from permissions (already in sessionStorage)
      const modeInfo = detectUserRoles();
      setUserMode(modeInfo);

      setIsAuthenticated(true);

      // Track login event in Google Analytics
      trackLogin(
        result.user.id, // User ID (sub)
        'email', // Login method
        modeInfo.currentMode // User role (admin/learner)
      );

      // Set Sentry user context. Tenant identification is handled by the
      // 'host' tag applied at Sentry.init() — no per-login tenant tag needed.
      setSentryUser({ id: result.user.id, email: result.user.email, name: result.user.full_name || result.user.given_name });

      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    // Capture user ID before clearing session for analytics
    const userId = user?.id;
    // Decided before the session is cleared: LMS sessions end on the LTI page.
    const signedOutPath = authService.getSignedOutPath();

    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Track logout event in Google Analytics before clearing user data
      if (userId) {
        trackLogout(userId);
      }

      setSentryUser(null);
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      sessionStorage.removeItem('permissions');

      // Clear browser history to prevent back button navigation to secure pages
      window.history.replaceState(null, '', signedOutPath);
      window.location.replace(signedOutPath);
    }
  };

  /**
   * Re-read permissions from the server and rewrite the cache.
   *
   * checkAuth short-circuits when a user is already in memory, so it cannot be
   * used to recover a cache that is empty or absent. This always calls /me,
   * which is what the access-denied screen needs before it can route anywhere.
   */
  const refreshPermissions = async (): Promise<boolean> => {
    try {
      const currentUserData = await authService.getCurrentUser();
      if (!currentUserData) return false;

      sessionStorage.setItem(
        'permissions',
        JSON.stringify(currentUserData.permissions || []),
      );
      setUserMode(detectUserRoles());
      return true;
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
      return false;
    }
  };

  const checkAuth = async (): Promise<boolean> => {
    // Wait for initializeAuth to finish if it's still in flight (prevents duplicate /me calls)
    if (initPromiseRef.current) {
      await initPromiseRef.current;
    }

    try {
      // If we already have user state in memory, we're good
      if (user && authService.isAuthenticated()) {
        return true;
      }

      // If no local session indicators and not an SSO callback, bail early (no network call)
      const isSsoCallback = window.location.pathname === AUTH_HANDLE_TOKEN;
      if (!authService.isAuthenticated() && !isSsoCallback) {
        handleUnauthenticated();
        return false;
      }

      // Local session exists but user state is null (e.g., new tab), or SSO callback.
      // Validate with server via GET /me (CSRF exempt).
      const currentUserData = await authService.getCurrentUser();
      if (currentUserData) {
        setUser(currentUserData.user);
        setTenantKey(currentUserData.tenant_key || null);
        setSentryUser({
          id: currentUserData.user.id,
          email: currentUserData.user.email,
          name: currentUserData.user.full_name || currentUserData.user.given_name,
        });

        // Store permissions BEFORE setting authenticated
        sessionStorage.setItem('permissions', JSON.stringify(currentUserData.permissions || []));

        // Derive user mode from permissions
        const modeInfo = detectUserRoles();
        setUserMode(modeInfo);

        setIsAuthenticated(true);
        return true;
      } else {
        handleUnauthenticated();
        return false;
      }
    } catch (error) {
      console.error('Auth check error:', error);
      handleUnauthenticated();
      return false;
    }
  };

  /**
   * Check tenant status - used for periodic validation
   * Returns false if tenant is not ACTIVE (triggers logout and redirect)
   */
  const checkTenantStatus = async (): Promise<boolean> => {
    // Skip check for management tenant
    if (isManagementTenant) {
      return true;
    }

    try {
      const status = await tenantStatusService.checkStatus();
      setTenantStatus(status.status);

      if (!status.can_login) {
        console.warn(`[AuthContext] Tenant status changed to ${status.status}, logging out user`);

        // Tenant is no longer ACTIVE - log out and redirect
        await logout();
        // Only redirect if not already on tenant-blocked page (prevents infinite loop)
        if (!window.location.pathname.includes('/tenant-blocked')) {
          window.location.replace('/tenant-blocked');
        }

        return false;
      }

      return true;
    } catch (error) {
      console.error('[AuthContext] Failed to check tenant status:', error);
      // On error, allow continued access - backend will catch invalid requests
      return true;
    }
  };

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return authService.hasAnyRole(roles);
  };

  const hasAllRoles = (roles: string[]): boolean => {
    return authService.hasAllRoles(roles);
  };

  const getUserGroups = (): string[] => {
    return authService.getUserGroups();
  };

  const getUserRoles = (): string[] => {
    return authService.getUserRoles();
  };

  const switchMode = (mode: UserMode) => {
    if (!canSwitchMode()) return;
    
    setUserMode(prev => ({
      ...prev,
      currentMode: mode,
    }));
    
    // Persist the mode selection
    localStorage.setItem('skillzen_user_mode', mode);
  };

  const canSwitchMode = (): boolean => {
    return userMode.hasBothRoles;
  };

  // Merge-update the current user in both React state and localStorage
  // so UI (header avatar, bell, etc.) reflects profile edits without re-login.
  const updateUser = (partial: Partial<UserInfo>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...partial } as UserInfo;
      try { authService.setUser(next); } catch { /* no-op */ }
      return next;
    });
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    userMode,
    tenantKey,
    tenantStatus,
    isManagementTenant,
    login,
    logout,
    checkAuth,
    refreshPermissions,
    checkTenantStatus,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getUserGroups,
    getUserRoles,
    switchMode,
    canSwitchMode,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;